import { Blocks, GitBranch } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageShell } from '@/components/layout/PageShell'
import { PipelineSelector } from '@/components/pipeline'
import { PipelineActivitySection } from '@/components/pipeline/PipelineActivitySection'
import { PipelineStageCard } from '@/components/pipeline/PipelineStageCard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePipelineActivities } from '@/features/cases/hooks/usePipelineActivities'

const STAGE_CARDS = [
  {
    stage: 'extract' as const,
    title: 'Extract',
    description:
      'Pull content out of source files — Docling for text and tables, vision-LLM for images and identity documents, WhisperX for audio, and structured parsers for CSV/XLSX.',
  },
  {
    stage: 'transform' as const,
    title: 'Transform',
    description:
      'Reshape the extracted payload — semantic chunking, audio chunk enrichment, schema validation, entity resolution, and pre-run cleanup of stale graph or index state.',
  },
  {
    stage: 'load' as const,
    title: 'Load',
    description:
      'Persist results — Neo4j document, chunk and graph nodes for case extraction; Typesense document and chunk indexing plus Parquet uploads for lake search.',
  },
]

export default function PipelinesPage() {
  const { t } = useTranslation()
  const { extractActivities, transformActivities, loadActivities, isLoading } =
    usePipelineActivities()

  return (
    <PageShell
      layout="padded"
      icon={GitBranch}
      title={t('cases.pipelinesPage.title')}
      summary={t('cases.pipelinesPage.summary')}
      guide={{
        to: '/docs/pipelines',
        label: t('cases.pipelinesPage.guideLabel'),
        hint: t('cases.pipelinesPage.guideHint'),
      }}
    >
      <Tabs defaultValue="pipelines" className="space-y-6">
        <TabsList>
          <TabsTrigger value="pipelines">
            <GitBranch className="size-4 mr-2" />
            Pipelines
          </TabsTrigger>
          <TabsTrigger value="activities">
            <Blocks className="size-4 mr-2" />
            Activities
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipelines" className="space-y-6">
          <PipelineSelector onSelect={() => undefined} />
          <div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-3 md:gap-6">
            {STAGE_CARDS.map(c => (
              <PipelineStageCard
                key={c.stage}
                stage={c.stage}
                title={c.title}
                description={c.description}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="activities" className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full size-8 border-b-2 border-primary" />
            </div>
          ) : (
            <div className="space-y-8">
              <PipelineActivitySection
                title="Extract Activities"
                stage="extract"
                activities={extractActivities}
              />
              <PipelineActivitySection
                title="Transform Activities"
                stage="transform"
                activities={transformActivities}
              />
              <PipelineActivitySection
                title="Load Activities"
                stage="load"
                activities={loadActivities}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageShell>
  )
}
